import type { Fault } from '@/types';

// 风电机组 3 类核心故障
// 严格按已批准的时间轴 SOP 编排；expertPath 通过 hints[] 落地为可读的"专家路径"
// 易错点检测通过 hints 里"⚠️" 前缀 + steps 中 correct=false 的错误动作实现

export const windturbineFaults: Fault[] = [
  // ============= 故障 1：齿轮箱过热（gearbox-overheat）=============
  // 场景：齿轮箱油温飙升到 95°C，如果不及时降载 + 启备用冷却，齿轮齿面会胶合损坏
  // 专家 S 级路径 5 步约 2 分钟：
  //   T=0s   view GEARBOX
  //   T=5s   pause 15s（观察温度趋势）
  //   T=20s  action POWER 2.5 → 1.5 MW（降有功功率）
  //   T=21s  action 启动备用冷却风扇（工艺按钮）
  //   T=22s  pause 60s（等油温下降）
  //   T=82s  view GEARBOX（确认 T-GB < 80）
  //   T=90s  通关
  {
    id: 'WTF001',
    name: '齿轮箱过热（gearbox-overheat）',
    description:
      '齿轮箱油温飙升到 95°C，冷却风扇可能堵塞或润滑油劣化。如果不及时降载 + 启备用冷却，' +
      '齿轮齿面会胶合损坏，严重时导致齿轮箱报废。',
    cause: '冷却器散热片堵塞 + 润滑油使用周期到期，散热能力下降，热量持续累积。',
    affectedEquipments: ['GEARBOX', 'GEN', 'CONVERTER'],
    symptoms: [
      { equipmentId: 'GEARBOX', param: 'T-GB', paramName: '齿轮箱油温', value: 95, unit: '°C', normal: '50-80', trend: 'up' },
      { equipmentId: 'GEARBOX', param: 'VIB-GB', paramName: '齿轮箱振动', value: 7.2, unit: 'mm/s', normal: '<4.5', trend: 'up' },
      { equipmentId: 'GEN', param: 'T-GEN', paramName: '绕组温度', value: 108, unit: '°C', normal: '<120', trend: 'up' },
    ],
    steps: [
      // 专家 S 级路径 —— 严格按时间轴顺序
      { id: 's1', action: '观察齿轮箱温度趋势 15 秒', correct: true, order: 1 },
      { id: 's2', action: '降有功功率 POWER 从 2.5 降到 1.5 MW', correct: true, order: 2 },
      { id: 's3', action: '启动备用冷却风扇（GEARBOX 工艺按钮）', correct: true, order: 3 },
      { id: 's4', action: '等 60 秒观察油温下降', correct: true, order: 4 },
      { id: 's5', action: '确认 T-GB 回到 80 以下通关', correct: true, order: 5 },
      // 易错动作 —— 不降温直接启冷却：温差大导致齿面热裂
      { id: 's6', action: '不降温直接启备用冷却（错误：温差大导致齿面热裂）', correct: false, order: 6 },
      { id: 's7', action: '不降载继续满功率运行（错误：热源不减油温继续升）', correct: false, order: 7 },
    ],
    hints: [
      '专家 S 级路径：view GEARBOX(0s) → 观察 15s → 降功率到 1.5MW(20s) → 启备用冷却(21s) → 等 60s → 确认 T-GB<80(90s) → 通关',
      '齿轮箱过热抢救原则：先降载减热源，再启冷却 —— 顺序不能反',
      '⚠️ 易错点：不降温直接启冷却 = 温差大导致齿面热裂',
    ],
    divergence: {
      drivers: [
        // 不干预时齿轮箱温度持续上升；振动也跟着恶化
        { equipmentId: 'GEARBOX', param: 'T-GB', rate: 0.3, cap: 120, delaySec: 10 },
        { equipmentId: 'GEARBOX', param: 'VIB-GB', rate: 0.05, cap: 12, delaySec: 15 },
      ],
    },
  },

  // ============= 故障 2：桨距角不对称（pitch-asymmetry）=============
  // 场景：三只叶片桨距角不一致，叶轮严重不平衡，塔筒振动急剧上升
  // 专家 S 级路径 5 步约 1.5 分钟：
  //   T=0s   view BLADE-A / BLADE-B / BLADE-C
  //   T=3s   action RPM 15 → 5（紧急降转速）
  //   T=4s   action PITCH-A/B/C 同步到统一 8°
  //   T=5s   pause 30s（等振动回落）
  //   T=35s  view SENSOR-VIB（确认 VIB-TOWER < 2.5）
  //   T=40s  通关
  {
    id: 'WTF002',
    name: '桨距角不对称（pitch-asymmetry）',
    description:
      '⚠️ 极危故障！三只叶片桨距角不一致（A=8°, B=3°, C=12°），叶轮严重不平衡，' +
      '塔筒振动急剧上升，若不及时同步桨距并紧急降速，可能导致叶片断裂或塔筒倒塌。',
    cause: 'B 叶片变桨轴承卡滞 + 变桨电机编码器漂移，导致三只叶片桨距角无法同步。',
    affectedEquipments: ['BLADE-A', 'BLADE-B', 'BLADE-C', 'HUB', 'SENSOR-VIB'],
    symptoms: [
      { equipmentId: 'BLADE-A', param: 'PITCH-A', paramName: '桨距角A', value: 8, unit: '°', normal: '0-15', trend: 'up' },
      { equipmentId: 'BLADE-B', param: 'PITCH-B', paramName: '桨距角B', value: 3, unit: '°', normal: '0-15', trend: 'down' },
      { equipmentId: 'BLADE-C', param: 'PITCH-C', paramName: '桨距角C', value: 12, unit: '°', normal: '0-15', trend: 'up' },
      { equipmentId: 'SENSOR-VIB', param: 'VIB-TOWER', paramName: '塔筒振动', value: 4.8, unit: 'mm/s', normal: '<2.5', trend: 'up' },
      { equipmentId: 'HUB', param: 'VIB-HUB', paramName: '轮毂振动', value: 8.5, unit: 'mm/s', normal: '<4.5', trend: 'up' },
    ],
    steps: [
      // 专家 S 级路径 —— 5 步紧急处置
      { id: 's1', action: '观察三只叶片桨距角差异', correct: true, order: 1 },
      { id: 's2', action: '紧急降转速 RPM 从 15 降到 5', correct: true, order: 2 },
      { id: 's3', action: '同步三只叶片桨距到统一 8°', correct: true, order: 3 },
      { id: 's4', action: '等 30 秒观察振动回落', correct: true, order: 4 },
      { id: 's5', action: '确认 VIB-TOWER < 2.5 通关', correct: true, order: 5 },
      // 易错动作 —— 不降速直接调桨距：叶轮负载突变可能断裂
      { id: 's6', action: '不降速直接调桨距（错误：叶轮负载突变可能断裂）', correct: false, order: 6 },
      { id: 's7', action: '只调一只叶片角度（错误：无法恢复平衡）', correct: false, order: 7 },
    ],
    hints: [
      '专家 S 级路径：view 叶片(0s) → 降转速到 5(3s) → 同步桨距到 8°(4s) → 等 30s → 确认 VIB-TOWER<2.5(40s) → 通关',
      '桨距不对称铁律：先降速减动能，再同步桨距，绝不能不降速直接调桨！',
      '⚠️ 易错点：不降速直接调桨距 = 叶轮负载突变可能断裂',
    ],
    divergence: {
      drivers: [
        // 不降速的话塔筒振动和轮毂振动都会失控上升；载荷逐渐降为零
        { equipmentId: 'SENSOR-VIB', param: 'VIB-TOWER', rate: 0.08, cap: 10, delaySec: 5 },
        { equipmentId: 'HUB', param: 'VIB-HUB', rate: 0.12, cap: 15, delaySec: 5 },
        { equipmentId: 'HUB', param: 'LOAD', rate: -0.02, cap: 0, delaySec: 10 },
      ],
    },
  },

  // ============= 故障 3：偏航锁定失效（yaw-lock-failure）=============
  // 场景：偏航系统未能对准主风向，偏航制动器未完全释放，产生异常摩擦热
  // 专家 S 级路径 6 步约 2.5 分钟：
  //   T=0s   view YAW / WIND
  //   T=5s   action 释放偏航制动器（YAW 工艺按钮）
  //   T=6s   action 手动偏航对风到 WIND-DIR ±5° 以内
  //   T=7s   action 重新锁定偏航制动器
  //   T=8s   pause 40s（等功率回升）
  //   T=48s  view GEN（确认 POWER > 2.0）
  //   T=55s  通关
  {
    id: 'WTF003',
    name: '偏航锁定失效（yaw-lock-failure）',
    description:
      '偏航系统未能对准主风向（偏航角偏差 35°），导致发电功率大幅下降，' +
      '同时偏航制动器未完全释放，产生异常摩擦热。需要手动解刹 + 偏航对风 + 再锁定。',
    cause: '偏航制动器液压压力不足 + 风向突变后偏航电机力矩不够，导致无法自动对风。',
    affectedEquipments: ['YAW', 'GEN', 'CONVERTER', 'WIND'],
    symptoms: [
      { equipmentId: 'YAW', param: 'YAW-POS', paramName: '偏航角度', value: 35, unit: '°', normal: '<10', trend: 'up' },
      { equipmentId: 'GEN', param: 'POWER', paramName: '有功功率', value: 0.8, unit: 'MW', normal: '1.5-3.5', trend: 'down' },
      { equipmentId: 'YAW', param: 'YAW-RATE', paramName: '偏航速率', value: 0, unit: '°/s', normal: '0.1-1.0', trend: 'down' },
      { equipmentId: 'CONVERTER', param: 'DC-VOLT', paramName: '直流母线电压', value: 980, unit: 'V', normal: '950-1200', trend: 'down' },
    ],
    steps: [
      // 专家 S 级路径
      { id: 's1', action: '观察偏航角度与风向偏差', correct: true, order: 1 },
      { id: 's2', action: '释放偏航制动器（YAW 工艺按钮）', correct: true, order: 2 },
      { id: 's3', action: '手动偏航对风到 WIND-DIR ±5° 以内', correct: true, order: 3 },
      { id: 's4', action: '重新锁定偏航制动器', correct: true, order: 4 },
      { id: 's5', action: '等 40 秒观察功率回升', correct: true, order: 5 },
      { id: 's6', action: '确认 POWER > 2.0 通关', correct: true, order: 6 },
      // 易错动作 —— 未解刹就强制偏航：电机烧毁
      { id: 's7', action: '未解刹就强制偏航（错误：电机烧毁）', correct: false, order: 7 },
      { id: 's8', action: '对风后不重新锁定（错误：风向再变功率又掉）', correct: false, order: 8 },
    ],
    hints: [
      '专家 S 级路径：view YAW/WIND(0s) → 释放制动器(5s) → 手动对风(6s) → 重新锁定(7s) → 等 40s → 确认 POWER>2.0(55s) → 通关',
      '偏航处置铁律：先解刹，再对风，最后锁定 —— 顺序不能乱',
      '⚠️ 易错点：未解刹就强制偏航 = 电机烧毁',
    ],
    divergence: {
      drivers: [
        // 不干预时功率持续下降；直流母线电压随机波动偏离
        { equipmentId: 'GEN', param: 'POWER', rate: -0.02, cap: 0.2, delaySec: 10 },
        { equipmentId: 'CONVERTER', param: 'DC-VOLT', rate: 5, cap: 1100, delaySec: 15 },
      ],
    },
  },

  // ============= 故障 4：偏航系统故障（yaw-system-fault）=============
  // 场景：偏航角度偏差 15° 且持续上升，偏航位置停滞 90°，发电功率降至 1.2MW
  // 专家 S 级路径 4 步：检查偏航偏差 → 手动对风 → 查偏航电机 → 降功率减载
  {
    id: 'WTF004',
    name: '偏航系统故障',
    description:
      '风机偏航角度持续偏差，无法对准风向，发电效率大幅下降。',
    cause: '偏航轴承润滑失效，偏航驱动电机过载保护跳闸，风机无法自动对风。',
    affectedEquipments: ['YAW', 'GEN'],
    symptoms: [
      { equipmentId: 'YAW', param: 'YAW-ERR', paramName: '偏航偏差',   value: 15, unit: '°',  normal: '0-5',    trend: 'up' },
      { equipmentId: 'YAW', param: 'YAW-POS', paramName: '偏航角度',   value: 90, unit: '°',  normal: '0-10',   trend: 'up' },
      { equipmentId: 'GEN', param: 'POWER',  paramName: '有功功率',   value: 1.2, unit: 'MW', normal: '1.5-3.5', trend: 'down' },
    ],
    steps: [
      { id: 's1', action: '检查偏航角度偏差趋势',   correct: true,  order: 1 },
      { id: 's2', action: '手动启动偏航对风',       correct: true,  order: 2 },
      { id: 's3', action: '检查偏航驱动电机状态',   correct: true,  order: 3 },
      { id: 's4', action: '降低功率输出减少载荷',   correct: true,  order: 4 },
      { id: 's5', action: '提高偏航速度快速对风',   correct: false, order: 5 },
      { id: 's6', action: '关闭偏航系统',           correct: false, order: 6 },
      { id: 's7', action: '解锁偏航制动器',         correct: false, order: 7 },
    ],
    hints: [
      '偏航偏差这么大，功率肯定上不去',
      '偏航电机跳闸了就手动对风，别让风机侧风运行',
      '润滑不好偏航轴承会卡死，定期检查油位',
    ],
    divergence: {
      drivers: [
        // 不干预时偏航偏差持续扩大，风机长期侧风运行
        { equipmentId: 'YAW', param: 'YAW-ERR', rate: 0.1, cap: 30, delaySec: 20 },
      ],
    },
  },

  // ============= 故障 5：变流器过流（converter-overcurrent）=============
  // 场景：直流母线电压升至 1350V、电流升至 1200A，功率波动 0.8-1.8MW
  // 专家 S 级路径 4 步：查直流母线电压 → 降有功功率 → 查 IGBT 散热 → 切备用通道
  {
    id: 'WTF005',
    name: '变流器过流',
    description:
      '变流器直流母线过流报警，功率输出不稳定，严重时可能烧毁IGBT模块。',
    cause: '变流器IGBT散热风扇故障，结温过高导致开关特性劣化，触放过流保护。',
    affectedEquipments: ['CONVERTER', 'GEN'],
    symptoms: [
      { equipmentId: 'CONVERTER', param: 'DC-VOLT', paramName: '直流母线电压', value: 1350, unit: 'V', normal: '950-1200',  trend: 'up' },
      { equipmentId: 'CONVERTER', param: 'CURR',    paramName: '直流母线电流', value: 1200, unit: 'A', normal: '600-900',   trend: 'up' },
      { equipmentId: 'GEN',       param: 'POWER',   paramName: '有功功率',     value: 0.8,  unit: 'MW', normal: '1.5-3.5',  trend: 'unstable' },
    ],
    steps: [
      { id: 's1', action: '检查变流器直流母线电压', correct: true,  order: 1 },
      { id: 's2', action: '降低有功功率输出',       correct: true,  order: 2 },
      { id: 's3', action: '检查IGBT散热系统',       correct: true,  order: 3 },
      { id: 's4', action: '切换到备用变流器通道',   correct: true,  order: 4 },
      { id: 's5', action: '提高直流母线电压',       correct: false, order: 5 },
      { id: 's6', action: '关闭变流器保护',         correct: false, order: 6 },
      { id: 's7', action: '增大有功输出测试',       correct: false, order: 7 },
    ],
    hints: [
      '直流母线电压这么高，先降功率保设备',
      'IGBT过热多半是散热问题，查查风扇',
      '变流器过流不处理会烧模块，严重时要切备用通道',
    ],
    divergence: {
      drivers: [
        // 不干预时直流母线电压持续上升，逼近 IGBT 耐压极限
        { equipmentId: 'CONVERTER', param: 'DC-VOLT', rate: 2, cap: 1500, delaySec: 15 },
      ],
    },
  },
];
